/**
 * dealHealth.ts — the frontend half of 2.4 (P2-12): how a deal's SERVER-
 * computed health signals render. The signals themselves come from
 * internal/modules/sales/health — the ONE definition the 4.3 digest will
 * share — and nothing here re-derives a signal from dates.
 *
 * H-2: colour is keyed on SEVERITY, never on code. A code this bundle has
 * never seen renders its label in its severity's colour exactly like a known
 * one; there is no code→colour table to fall behind, and `code` is carried
 * only as a data attribute. Only the SEVERITY vocabulary is known here
 * (danger | warning → the existing token pairs). A severity outside it is
 * still rendered — the label is the server's statement and hiding it would
 * be dishonest — but in the neutral outline capsule with NO status colour,
 * never a guessed one.
 *
 * `health` on the wire: an ARRAY on an open deal ([] = healthy), NULL on a
 * closed deal (not evaluated), ABSENT on a read path that predates 2.4 or
 * never attaches it (the account-detail deal lists). All three render
 * nothing; the distinction is carried, not drawn (F2).
 */
import { danger, warning } from '../../../../theme/tokens'
import type { DealHealthSignal } from '../../../../api/types'

export type HealthTone = { background: string; color: string }

const SEVERITY_RANK: Record<string, number> = { danger: 0, warning: 1 }

/** The token pair for a severity, or null for one this bundle does not know
 *  (rendered neutral — never a default colour). */
export function healthTone(severity: string): HealthTone | null {
  if (severity === 'danger') return { background: danger.bg, color: danger.text }
  if (severity === 'warning') return { background: warning.bg, color: warning.text }
  return null
}

/** Severity order: danger, warning, then anything unknown — STABLE, so the
 *  server's worst-first order survives within a severity and this is a no-op
 *  on a correctly ordered array. */
export function worstFirst(signals: readonly DealHealthSignal[]): DealHealthSignal[] {
  return signals
    .map((s, i) => ({ s, i }))
    .sort((a, b) => ((SEVERITY_RANK[a.s.severity] ?? 99) - (SEVERITY_RANK[b.s.severity] ?? 99)) || a.i - b.i)
    .map(x => x.s)
}

/** What there is to render: the signals of an open deal, worst first; empty
 *  for [] (healthy), null (closed) and undefined (not on this wire). */
export function visibleSignals(health: readonly DealHealthSignal[] | null | undefined): DealHealthSignal[] {
  if (!health || health.length === 0) return []
  return worstFirst(health)
}

/** The board card's ONE badge: the worst signal and how many more it stands for. */
export function cardBadge(health: readonly DealHealthSignal[] | null | undefined): { signal: DealHealthSignal; more: number } | null {
  const v = visibleSignals(health)
  if (v.length === 0) return null
  return { signal: v[0], more: v.length - 1 }
}

/** "29 Aug" — the year only when it is not the current one; '' for a stamp
 *  that does not parse (never a fabricated date). UTC, like every other date
 *  the dashboard prints from the wire. */
export function fmtSince(iso: string, now: number = Date.now()): string {
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return ''
  const d = new Date(t)
  const sameYear = d.getUTCFullYear() === new Date(now).getUTCFullYear()
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', ...(sameYear ? {} : { year: 'numeric' }), timeZone: 'UTC' })
}
